/*
  My Football Store · mfs2 components (design system 2.0)
  Loaded as a module by snippets/mfs2-assets.liquid, so it runs once per page.

  Custom elements:
    <mfs2-buy-form>    variant selection, Add to bag (native Horizon cart events), Buy now
    <mfs2-sticky-atc>  mobile sticky bar that mirrors a buy form
    <mfs2-gallery>     swipe gallery with dots and thumbnails
    <mfs2-tabs>        tabbed product shelf
    <mfs2-ugc>         customer videos: muted autoplay in the grid, sound on in the viewer
    <mfs2-reco>        loads Shopify product recommendations into a rail
    <mfs2-delivery>    "Get it by …" date range from working days
*/

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---------- Money ---------- */
function formatMoney(cents, format) {
  const fmt = format || 'Rs. {{amount_no_decimals}}';
  const n = Number(cents) / 100;
  const group = (value, decimals, thousands, decimal) => {
    const fixed = value.toFixed(decimals);
    const [whole, frac] = fixed.split('.');
    const withSep = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return frac ? withSep + decimal + frac : withSep;
  };
  return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals': return group(n, 0, ',', '.');
      case 'amount_with_comma_separator': return group(n, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return group(n, 0, '.', ',');
      case 'amount_with_apostrophe_separator': return group(n, 2, "'", '.');
      default: return group(n, 2, ',', '.');
    }
  });
}

/* ---------- Cart (reuses Horizon's cart drawer + count through @shopify/events) ---------- */
// opts.items: add several lines in one request (e.g. shirt + name printing), as
// [{ id, quantity, properties }]. opts.openDrawer: false keeps the drawer shut (Buy now).
async function addToCart(form, variantId, quantity, sourceEl, opts = {}) {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const ids = [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);
  const items = opts.items || null;

  let body;
  const headers = { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' };
  if (items) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(ids.length ? { items, sections: ids.join(',') } : { items });
  } else {
    body = new FormData(form);
    body.set('id', String(variantId));
    body.set('quantity', String(quantity || 1));
    if (ids.length) body.append('sections', ids.join(','));
  }
  const lines = (items || [{ id: variantId, quantity: quantity || 1 }])
    .map((it) => ({ merchandiseId: String(it.id), quantity: Number(it.quantity || 1) }));

  let events = null;
  try { events = await import('@shopify/events'); } catch (e) { events = null; }

  let deferred = null;
  if (events && events.CartLinesUpdateEvent) {
    deferred = events.CartLinesUpdateEvent.createPromise();
    (sourceEl || form).dispatchEvent(new events.CartLinesUpdateEvent({
      action: 'add', context: 'product', lines, promise: deferred.promise,
    }));
  }

  const res = await fetch(`${root}cart/add.js`, { method: 'POST', headers, body });
  const data = await res.json();
  if (!res.ok || data.status) {
    if (deferred) deferred.resolve({ cart: null, detail: { didError: true } });
    throw new Error(data.description || data.message || 'Could not add to bag');
  }
  if (deferred) {
    const cart = await (await fetch(`${root}cart.js`)).json();
    deferred.resolve({
      cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, itemCount: lines.reduce((n, l) => n + l.quantity, 0), sections: data.sections, didError: false },
    });
  }
  // Inside the video viewer (a modal dialog) keep the shopper in place; the bag count still updates.
  const inModal = sourceEl && sourceEl.closest && sourceEl.closest('dialog[open]');
  const drawer = document.getElementById('cart-drawer');
  if (opts.openDrawer !== false && !inModal && drawer && typeof drawer.open === 'function') {
    try { drawer.open(); } catch (e) { /* drawer may already be open */ }
  }
  return data;
}

/* ---------- Dialog open/close helpers (size guide etc.) ---------- */
document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-open-dialog]');
  if (opener) {
    const dialog = document.getElementById(opener.dataset.openDialog);
    if (dialog && typeof dialog.showModal === 'function') dialog.showModal();
    return;
  }
  const closer = event.target.closest('[data-close-dialog]');
  if (closer) closer.closest('dialog')?.close();
});
document.addEventListener('click', (event) => {
  if (event.target instanceof HTMLDialogElement && event.target.classList.contains('mfs2-dialog')) event.target.close();
});

/* ---------- <mfs2-buy-form> ---------- */
class Mfs2BuyForm extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.form = this.querySelector('form');
    this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
    this.moneyFormat = this.dataset.moneyFormat;
    this.idInput = this.querySelector('[data-variant-input]');
    this.errorEl = this.querySelector('[data-error]');
    this.statusEl = this.querySelector('[data-status]');
    this.addBtn = this.querySelector('[data-add]');
    this.buyBtn = this.querySelector('[data-buy]');
    this.optionCount = (this.variants[0] && this.variants[0].options.length) || 0;
    this.setupPrint();

    this.form.addEventListener('change', () => this.update());
    this.form.addEventListener('submit', (e) => { e.preventDefault(); this.add(); });
    if (this.buyBtn) this.buyBtn.addEventListener('click', () => this.buy());
    this.update();
  }

  // Name printing: a checkbox that opens a name field; adds the "Name printing" product with the shirt.
  setupPrint() {
    this.print = this.querySelector('[data-print]');
    if (!this.print) return;
    this.printToggle = this.print.querySelector('[data-print-toggle]');
    this.printFields = this.print.querySelector('[data-print-fields]');
    this.printName = this.print.querySelector('[data-print-name]');
    this.printCount = this.print.querySelector('[data-print-count]');
    this.printToggle.addEventListener('change', () => {
      const on = this.printToggle.checked;
      this.printFields.hidden = !on;
      this.printName.disabled = !on; // a disabled field is left out of the form
      this.printToggle.setAttribute('aria-expanded', String(on));
      this.print.classList.toggle('is-on', on);
      if (on) this.printName.focus({ preventScroll: true });
      else this.printName.classList.remove('is-error');
    });
    this.printName.addEventListener('input', () => {
      const clean = this.printName.value.toUpperCase().replace(/[^A-Z .'-]/g, '').replace(/\s{2,}/g, ' ');
      if (clean !== this.printName.value) this.printName.value = clean;
      if (this.printCount) this.printCount.textContent = clean.length;
      if (clean.trim()) { this.printName.classList.remove('is-error'); this.showError(''); }
    });
  }

  printOn() { return !!(this.printToggle && this.printToggle.checked); }
  printText() { return this.printOn() ? this.printName.value.trim() : ''; }
  extraPrice() { return this.printOn() ? Number(this.print.dataset.printPrice || 0) : 0; }

  // Shirt line (with the name) + the name printing line, linked by a shared reference.
  printItems(variant) {
    const ref = `P${Date.now().toString(36).toUpperCase()}`;
    const name = this.printText();
    return [
      { id: variant.id, quantity: 1, properties: { 'Name on back': name, _print_ref: ref } },
      { id: Number(this.print.dataset.printVariant), quantity: 1, properties: { For: `${this.dataset.productTitle} · ${variant.title}`, Name: name, _print_ref: ref } },
    ];
  }

  selected() {
    const values = new Array(this.optionCount).fill(null);
    this.querySelectorAll('[data-fixed][data-option-index]').forEach((el) => { values[+el.dataset.optionIndex] = el.value; });
    this.querySelectorAll('input[type="radio"][data-option-index]:checked').forEach((el) => { values[+el.dataset.optionIndex] = el.value; });
    return values;
  }

  missingGroups() {
    const values = this.selected();
    return [...this.querySelectorAll('[data-option-group]')].filter((g) => values[+g.dataset.optionGroup] === null);
  }

  variant() {
    const values = this.selected();
    if (values.some((v) => v === null)) return null;
    return this.variants.find((v) => v.options.every((o, i) => o === values[i])) || null;
  }

  // Cheapest matching variant while a size is still open, so the price shown is honest.
  previewVariant() {
    const values = this.selected();
    const matches = this.variants.filter((v) => v.options.every((o, i) => values[i] === null || o === values[i]));
    return matches.sort((a, b) => a.price - b.price)[0] || this.variants[0];
  }

  update() {
    const values = this.selected();
    const variant = this.variant();
    const shown = variant || this.previewVariant();

    // Option availability (given the other choices) + chosen labels
    this.querySelectorAll('[data-option-group]').forEach((group) => {
      const idx = +group.dataset.optionGroup;
      group.querySelectorAll('.mfs2-pill').forEach((pill) => {
        const val = pill.dataset.value;
        const ok = this.variants.some((v) => v.available && v.options[idx] === val &&
          v.options.every((o, i) => i === idx || values[i] === null || o === values[i]));
        pill.classList.toggle('is-unavailable', !ok);
      });
      const chosen = group.querySelector('[data-chosen]');
      if (chosen) chosen.textContent = values[idx] ? '· ' + values[idx] : '';
      if (values[idx] !== null) group.querySelector('.mfs2-pills')?.classList.remove('is-error');
      group.querySelectorAll('[data-note-for]').forEach((li) => { li.hidden = li.dataset.noteFor !== values[idx]; });
    });

    if (variant) this.idInput.value = variant.id;
    const total = this.querySelector('[data-total]');
    if (total) total.textContent = formatMoney(shown.price + this.extraPrice(), this.moneyFormat);

    const soldOut = variant && !variant.available;
    [this.addBtn, this.buyBtn].forEach((btn) => { if (btn) btn.disabled = !!soldOut; });
    const label = this.querySelector('[data-add-label]');
    if (label) label.textContent = soldOut ? 'Sold out' : 'Add to bag';
    if (this.missingGroups().length === 0) this.showError('');

    this.dispatchEvent(new CustomEvent('mfs2:variant', {
      bubbles: true,
      detail: {
        variant, complete: !!variant, soldOut: !!soldOut,
        price: formatMoney(shown.price + this.extraPrice(), this.moneyFormat),
        label: values.filter(Boolean).join(' · '),
      },
    }));
  }

  showError(text) {
    if (!this.errorEl) return;
    this.errorEl.textContent = text;
    this.errorEl.hidden = !text;
  }

  validate() {
    const missing = this.missingGroups();
    if (!missing.length) {
      if (this.printOn() && !this.printText()) {
        this.showError('Type the name to print, or untick name printing.');
        this.printName.classList.add('is-error');
        this.print.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
        setTimeout(() => this.printName.focus({ preventScroll: true }), 300);
        return false;
      }
      return true;
    }
    const names = missing.map((g) => (g.querySelector('legend')?.firstChild?.textContent || 'an option').trim().toLowerCase());
    this.showError(`Pick a ${names.join(' and ')} first.`);
    missing.forEach((g) => g.querySelector('.mfs2-pills')?.classList.add('is-error'));
    const first = missing[0].querySelector('input');
    missing[0].scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
    setTimeout(() => first && first.focus({ preventScroll: true }), 300);
    return false;
  }

  async add() {
    if (!this.validate()) return false;
    const variant = this.variant();
    if (!variant || !variant.available) return false;
    this.addBtn?.classList.add('is-loading');
    this.statusEl.textContent = '';
    try {
      const opts = this.printOn() ? { items: this.printItems(variant) } : {};
      await addToCart(this.form, variant.id, 1, this.addBtn || this, opts);
      this.statusEl.textContent = `Added to your bag: ${variant.title}${this.printOn() ? ` with "${this.printText()}"` : ''}.`;
      return true;
    } catch (err) {
      this.showError(err.message);
      return false;
    } finally {
      this.addBtn?.classList.remove('is-loading');
    }
  }

  buy() {
    if (!this.validate()) return;
    const variant = this.variant();
    if (!variant || !variant.available) return;
    this.idInput.value = variant.id;
    const btn = this.buyBtn || this.addBtn;
    btn?.classList.add('is-loading');

    // Shopify's own checkout: a cart permalink (/cart/<variant>:1) goes straight to checkout
    // with only this shirt; automatic discounts still apply.
    const root = this.dataset.root || '/';
    if (this.printOn()) { this.buyPrinted(variant, btn, root); return; }
    window.location.href = `${root}cart/${variant.id}:1`.replace('//cart', '/cart');
  }

  // A printed shirt is two cart lines with the name on them, so it goes through the cart to
  // Shopify's checkout (a permalink can't carry the name).
  async buyPrinted(variant, btn, root) {
    try {
      await addToCart(this.form, variant.id, 1, btn, { items: this.printItems(variant), openDrawer: false });
    } catch (err) {
      this.showError(err.message);
      btn?.classList.remove('is-loading');
      return;
    }
    window.location.href = `${root}checkout`.replace('//checkout', '/checkout');
  }
}
customElements.define('mfs2-buy-form', Mfs2BuyForm);

/* ---------- <mfs2-sticky-atc data-for="form-id"> ---------- */
class Mfs2StickyAtc extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.buyForm = document.querySelector(`mfs2-buy-form[data-form-id="${this.dataset.for}"]`);
    if (!this.buyForm) return;
    this.metaEl = this.querySelector('[data-sticky-meta]');
    this.priceEl = this.querySelector('[data-sticky-price]');
    this.buyLabel = this.querySelector('[data-sticky-buy-label]');
    this.inert = true;

    this.querySelector('[data-sticky-add]')?.addEventListener('click', () => this.act('add'));
    this.querySelector('[data-sticky-buy]')?.addEventListener('click', () => this.act('buy'));
    this.buyForm.addEventListener('mfs2:variant', (e) => this.sync(e.detail));

    const actions = this.buyForm.querySelector('[data-actions]');
    this.pastActions = false;
    this.footerVisible = false;
    new IntersectionObserver(([entry]) => {
      this.pastActions = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.render();
    }).observe(actions);
    const footer = document.querySelector('footer, .shopify-section-group-footer-group');
    if (footer) new IntersectionObserver(([entry]) => { this.footerVisible = entry.isIntersecting; this.render(); }).observe(footer);
    this.buyForm.update();
  }

  sync(detail) {
    if (this.priceEl) this.priceEl.textContent = detail.price;
    if (this.metaEl) this.metaEl.textContent = detail.label || this.metaEl.dataset.default || '';
    if (this.buyLabel) this.buyLabel.textContent = detail.soldOut ? 'Sold out' : (detail.complete ? 'Buy now' : 'Pick size');
    this.querySelectorAll('button').forEach((b) => { b.disabled = !!detail.soldOut; });
  }

  render() {
    const show = this.pastActions && !this.footerVisible && window.matchMedia('(max-width: 989px)').matches;
    this.classList.toggle('is-visible', show);
    this.inert = !show;
    this.setAttribute('aria-hidden', String(!show));
    document.documentElement.style.setProperty('--mfs2-sticky-h', show ? `${this.offsetHeight}px` : '0px');
  }

  act(kind) {
    if (!this.buyForm.variant()) { this.buyForm.validate(); return; }
    if (kind === 'buy') this.buyForm.buy(); else this.buyForm.add();
  }
}
customElements.define('mfs2-sticky-atc', Mfs2StickyAtc);

/* ---------- <mfs2-gallery> ---------- */
class Mfs2Gallery extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.track = this.querySelector('[data-track]');
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.dots = [...this.querySelectorAll('[data-dot]')];
    this.thumbs = [...this.querySelectorAll('[data-thumb]')];
    this.counter = this.querySelector('[data-counter]');
    if (!this.track) return;
    let raf;
    this.track.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => this.mark()); }, { passive: true });
    [...this.dots, ...this.thumbs].forEach((el) => el.addEventListener('click', () => this.go(+el.dataset.index)));
    this.mark();
  }
  go(i) {
    const slide = this.slides[i];
    if (!slide) return;
    this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
  }
  mark() {
    const w = this.track.clientWidth || 1;
    const i = Math.round(this.track.scrollLeft / w);
    [...this.dots, ...this.thumbs].forEach((el) => el.setAttribute('aria-current', String(+el.dataset.index === i)));
    if (this.counter) this.counter.textContent = `${i + 1} / ${this.slides.length}`;
  }
}
customElements.define('mfs2-gallery', Mfs2Gallery);

/* ---------- <mfs2-tabs> ---------- */
class Mfs2Tabs extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.select(i));
      tab.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        const next = (i + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
        this.select(next); this.tabs[next].focus();
      });
    });
  }
  select(i) {
    this.tabs.forEach((t, k) => { t.setAttribute('aria-selected', String(k === i)); t.tabIndex = k === i ? 0 : -1; });
    this.panels.forEach((p, k) => { p.hidden = k !== i; });
  }
}
customElements.define('mfs2-tabs', Mfs2Tabs);

/* ---------- <mfs2-ugc> customer videos ---------- */
class Mfs2Ugc extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.tiles = [...this.querySelectorAll('[data-ugc-tile]')];
    this.dialog = this.querySelector('[data-ugc-viewer]');
    this.slides = [...this.querySelectorAll('[data-ugc-slide]')];
    this.segs = [...this.querySelectorAll('[data-ugc-seg]')];
    this.muted = false;
    this.index = -1;

    // Grid: muted autoplay only while on screen; never for reduced-motion users.
    if (!reduceMotion && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => entries.forEach((entry) => {
        const v = entry.target.querySelector('video');
        if (!v) return;
        if (entry.isIntersecting && this.index < 0) { v.muted = true; v.play().catch(() => {}); } else v.pause();
      }), { threshold: 0.4 });
      this.tiles.forEach((t) => io.observe(t));
    }
    this.tiles.forEach((tile, i) => tile.addEventListener('click', () => this.open(i)));

    if (!this.dialog) return;
    this.dialog.addEventListener('close', () => this.onClose());
    this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.dialog.close(); });
    this.querySelector('[data-ugc-close]')?.addEventListener('click', () => this.dialog.close());
    this.querySelectorAll('[data-ugc-prev]').forEach((b) => b.addEventListener('click', () => this.show(this.index - 1)));
    this.querySelectorAll('[data-ugc-next]').forEach((b) => b.addEventListener('click', () => this.show(this.index + 1)));
    this.soundBtn = this.querySelector('[data-ugc-sound]');
    this.soundBtn?.addEventListener('click', () => { this.muted = !this.muted; this.applySound(); });
    this.slides.forEach((slide, i) => {
      const v = slide.querySelector('video');
      if (!v) return;
      v.addEventListener('timeupdate', () => {
        if (i !== this.index || !v.duration) return;
        const bar = this.segs[i]?.firstElementChild;
        if (bar) bar.style.transform = `scaleX(${v.currentTime / v.duration})`;
      });
      v.addEventListener('ended', () => { if (i === this.index) this.show(i + 1); });
    });
  }

  open(i) {
    if (!this.dialog) return;
    this.tiles.forEach((t) => t.querySelector('video')?.pause());
    this.muted = false; // opened by a tap, so sound starts on
    if (!this.dialog.open) this.dialog.showModal();
    this.show(i);
  }

  show(i) {
    if (i < 0) i = 0;
    if (i >= this.slides.length) { this.dialog.close(); return; }
    this.slides.forEach((s, k) => {
      const v = s.querySelector('video');
      s.hidden = k !== i;
      if (k !== i && v) v.pause();
    });
    this.segs.forEach((seg, k) => { const bar = seg.firstElementChild; if (bar) bar.style.transform = `scaleX(${k < i ? 1 : 0})`; });
    this.index = i;
    const v = this.slides[i].querySelector('video');
    if (v) {
      if (!v.src && v.dataset.src) v.src = v.dataset.src;
      v.currentTime = 0;
      this.applySound();
      v.play().catch(() => { this.muted = true; this.applySound(); v.play().catch(() => {}); });
    }
  }

  applySound() {
    const v = this.slides[this.index]?.querySelector('video');
    if (v) v.muted = this.muted;
    if (this.soundBtn) {
      this.soundBtn.setAttribute('aria-pressed', String(!this.muted));
      this.soundBtn.setAttribute('aria-label', this.muted ? 'Turn sound on' : 'Mute');
      this.soundBtn.querySelector('[data-icon-sound]').hidden = this.muted;
      this.soundBtn.querySelector('[data-icon-muted]').hidden = !this.muted;
    }
    const label = this.querySelector('[data-ugc-sound-label]');
    if (label) label.textContent = this.muted ? 'Muted' : 'Sound on';
  }

  onClose() {
    this.slides.forEach((s) => s.querySelector('video')?.pause());
    this.index = -1;
    if (!reduceMotion) this.tiles.forEach((t) => {
      const v = t.querySelector('video');
      const r = t.getBoundingClientRect();
      if (v && r.top < window.innerHeight && r.bottom > 0) { v.muted = true; v.play().catch(() => {}); }
    });
  }
}
customElements.define('mfs2-ugc', Mfs2Ugc);

/* ---------- <mfs2-reco data-url> ---------- */
class Mfs2Reco extends HTMLElement {
  connectedCallback() {
    if (this._ready || !this.dataset.url) return;
    this._ready = true;
    const load = async () => {
      try {
        const html = await (await fetch(this.dataset.url)).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector(`mfs2-reco[data-section="${this.dataset.section}"]`);
        if (fresh && fresh.querySelector('.mfs2-pcard')) this.innerHTML = fresh.innerHTML;
        else this.closest('.mfs2')?.setAttribute('hidden', '');
      } catch (e) { this.closest('.mfs2')?.setAttribute('hidden', ''); }
    };
    new IntersectionObserver((entries, io) => { if (entries[0].isIntersecting) { io.disconnect(); load(); } }, { rootMargin: '600px' }).observe(this);
  }
}
customElements.define('mfs2-reco', Mfs2Reco);

/* ---------- <mfs2-delivery data-min data-max> ---------- */
class Mfs2Delivery extends HTMLElement {
  connectedCallback() {
    const addWorking = (n) => {
      const d = new Date(); let k = 0;
      while (k < n) { d.setDate(d.getDate() + 1); const w = d.getDay(); if (w !== 0 && w !== 6) k++; }
      return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
    };
    const min = +this.dataset.min || 3;
    const max = +this.dataset.max || 5;
    const out = this.querySelector('[data-range]');
    if (out) out.textContent = `${addWorking(min)} – ${addWorking(max)}`;
  }
}
customElements.define('mfs2-delivery', Mfs2Delivery);

/* ---------- <mfs2-popup> welcome offer ---------- */
class Mfs2Popup extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.dialog = this.querySelector('dialog');
    this.teaser = this.querySelector('[data-popup-teaser]');
    this.key = this.dataset.key || 'mfs2-popup';
    if (!this.dialog) return;

    this.querySelectorAll('[data-popup-close]').forEach((b) => b.addEventListener('click', () => this.dialog.close()));
    this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.dialog.close(); });
    this.dialog.addEventListener('close', () => this.onClose());
    this.teaser?.addEventListener('click', () => this.open(true));
    this.querySelector('[data-popup-copy]')?.addEventListener('click', (e) => this.copy(e.currentTarget));

    // Theme editor: open while the section is selected.
    if (this.hasAttribute('data-design-mode')) {
      document.addEventListener('shopify:section:select', (e) => { if (e.detail.sectionId === this.dataset.sectionId) this.open(true); });
      document.addEventListener('shopify:section:deselect', (e) => { if (e.detail.sectionId === this.dataset.sectionId) this.dialog.close(); });
    }

    // Returning from the sign-up form: show the result straight away.
    const success = this.querySelector('[data-popup-success]');
    if (success || this.querySelector('[data-popup-error]')) {
      if (success) this.save({ done: true });
      this.open(true);
      return;
    }
    if (this.hasAttribute('data-design-mode')) return;

    const state = this.read();
    if (state.done) return;
    const days = +this.dataset.days || 14;
    if (state.closedAt && Date.now() - state.closedAt < days * 864e5) { this.showTeaser(true); return; }

    // Whichever comes first: time on page or scroll depth.
    const fire = () => {
      if (this._armed === false) return;
      this._armed = false;
      window.removeEventListener('scroll', onScroll);
      clearTimeout(timer);
      this.open(false);
    };
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max > 0 && (window.scrollY / max) * 100 >= (+this.dataset.scroll || 40)) fire();
    };
    const timer = setTimeout(fire, (+this.dataset.delay || 10) * 1000);
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  open(force) {
    // Never stack on top of another open dialog (cart drawer, size guide, video viewer).
    if (!force && document.querySelector('dialog[open]')) { setTimeout(() => this.open(false), 5000); return; }
    if (this.dialog.open) return;
    this.showTeaser(false);
    this.dialog.showModal();
    this.querySelector('input[type="email"]')?.focus({ preventScroll: true });
  }

  onClose() {
    const state = this.read();
    if (!state.done) { this.save({ closedAt: Date.now() }); this.showTeaser(true); }
  }

  showTeaser(show) { if (this.teaser) this.teaser.hidden = !show || this.read().done; }

  async copy(btn) {
    const code = this.querySelector('[data-popup-code]')?.textContent.trim();
    try { await navigator.clipboard.writeText(code); btn.textContent = btn.dataset.copied || 'Copied'; } catch (e) { /* user can copy by hand */ }
  }

  read() { try { return JSON.parse(localStorage.getItem(this.key)) || {}; } catch (e) { return {}; } }
  save(v) { try { localStorage.setItem(this.key, JSON.stringify(v)); } catch (e) { /* storage blocked */ } }
}
customElements.define('mfs2-popup', Mfs2Popup);

/* ---------- <mfs2-header> sticky shadow + measured height ---------- */
class Mfs2Header extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    const setHeight = () => document.body.style.setProperty('--header-height', `${this.offsetHeight}px`);
    setHeight();
    if ('ResizeObserver' in window) new ResizeObserver(setHeight).observe(this);
    if (this.hasAttribute('data-sticky')) {
      const onScroll = () => this.classList.toggle('is-scrolled', window.scrollY > 4);
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }
    // Close the mobile menu when a link inside it is followed.
    this.querySelectorAll('.mfs2-menu a').forEach((a) => a.addEventListener('click', () => a.closest('dialog')?.close()));
  }
}
customElements.define('mfs2-header', Mfs2Header);

/* Click on the dimmed backdrop closes dialogs marked data-backdrop-close (menu drawer). */
document.addEventListener('click', (event) => {
  const d = event.target;
  if (d instanceof HTMLDialogElement && d.hasAttribute('data-backdrop-close')) d.close();
});

/* Wishlist hearts: reflect saved items on load (toggling is handled by the theme's mfs.js). */
function mfs2SyncHearts() {
  let list = [];
  try { list = JSON.parse(localStorage.getItem('mfs-wishlist') || '[]'); } catch (e) { list = []; }
  document.querySelectorAll('.mfs2 [data-mfs-wishlist]').forEach((b) => b.setAttribute('aria-pressed', String(list.includes(b.dataset.mfsWishlist))));
}
mfs2SyncHearts();
document.addEventListener('mfs:wishlist', mfs2SyncHearts);


/* ---------- Tabs inside windows (size chart Fan fit / Player fit) ---------- */
function mfs2SelectTab(container, id) {
  container.querySelectorAll('[data-tab]').forEach((t) => t.setAttribute('aria-selected', String(t.dataset.tab === id)));
  container.querySelectorAll('[data-tab-panel]').forEach((p) => { p.hidden = p.dataset.tabPanel !== id; });
}
document.addEventListener('click', (event) => {
  const tab = event.target.closest('[data-tabs] [data-tab]');
  if (tab) { mfs2SelectTab(tab.closest('[data-tabs]'), tab.dataset.tab); return; }
  // Opening a size guide: show the tab that matches the version already chosen (Fan / Player).
  const opener = event.target.closest('[data-open-dialog]');
  if (!opener) return;
  const dialog = document.getElementById(opener.dataset.openDialog);
  const tabs = dialog && dialog.querySelector('[data-tabs]');
  const form = opener.closest('mfs2-buy-form');
  if (!tabs || !form) return;
  const version = form.querySelector('.mfs2-segment input:checked');
  const match = version && [...tabs.querySelectorAll('[data-tab]')].find((t) => (t.dataset.version || '').toLowerCase() === version.value.toLowerCase());
  if (match) mfs2SelectTab(tabs, match.dataset.tab);
});
